import React, { createContext, useState } from 'react';
import filter from 'lodash/filter';
import { useDispatch } from 'react-redux';

import { ButtonTypes } from '@ad-scripts/presentation/components/common/button';
import CampaignRowActions from 'src/presentation/components/pages/proactiveCommunications/components/CampaignRowActions';
import CampaignsEditor from 'src/presentation/components/pages/proactiveCommunications/components/shared/CampaignsEditor';
import campaignsResource, { CampaignType } from 'src/actions/campaigns/campaignsResource';
import EmailCampaignContentEditor from './proactiveEmail/EmailCampaignContentEditor';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import SmsCampaignContentEditor from './proactiveSms/SmsCampaignContentEditor';
import { StyledConfirmOrCancelModal } from 'src/presentation/components/pages/shared/rowActions/StyledConfirmOrCancelModal';
import TablePage from 'src/presentation/components/pages/shared/TablePage';
import useProactiveCommunication from 'src/presentation/components/pages/proactiveCommunications/hooks/useProactiveCommunication';
import VoiceCampaignContentEditor from './proactiveVoice/VoiceCampaignContentEditor';

type CampaignContextType = {
  campaignType: CampaignType;
  contentEditor?: JSX.Element;
};

export const CampaignContext = createContext<CampaignContextType>({ campaignType: CampaignType.SMS });

const columnConfig = [
  { attr: 'name', label: 'Campaign Name' },
  { attr: 'description', label: 'Description' },
  { attr: 'createdByString', label: 'Created By' },
  { attr: 'createdAtString', label: 'Created On' },
];

type ProactiveCommunicationType = {
  ({
    actionsRender,
    campaignType,
    description,
    emptyStateRender,
    header,
    root,
  }: {
    actionsRender: any;
    campaignType: CampaignType;
    description: string | JSX.Element;
    emptyStateRender: any;
    header: string;
    root: string;
  }): JSX.Element | null;
};

const ProactiveCommunication: ProactiveCommunicationType = ({
  actionsRender,
  campaignType,
  description,
  emptyStateRender,
  header,
  root,
}) => {
  const { campaigns, campaignsErrors, loadingState } = useProactiveCommunication({ campaignType });

  const dispatch = useDispatch();

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const openDeleteModal = deleteItemId => {
    setDeleteItem(deleteItemId);
    setIsDeleteModalVisible(true);
  };
  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
  };
  const handleDeleteSubmit = () => {
    dispatch(campaignsResource.effects.remove({ id: deleteItem }));
    setIsDeleteModalVisible(false);
  };

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }
    const results = filter(items, item => {
      return item.name.toLowerCase().indexOf(searchString.toLowerCase()) > -1;
    });
    return results;
  }

  const renderRowActions = ({ item }) => {
    return <CampaignRowActions openDeleteModal={openDeleteModal} item={item} root={root} />;
  };

  const context = {
    campaignType: campaignType,
    contentEditor: getEditor(campaignType),
  };

  return (
    <CampaignContext.Provider value={context}>
      <TablePage
        actions={actionsRender}
        columnConfig={columnConfig}
        editor={CampaignsEditor}
        errors={campaignsErrors}
        emptyStateRender={emptyStateRender}
        emptyStateFullHeightLayout
        filterFunc={filterFunc}
        isLoading={isLoadingStatus(loadingState)}
        resetErrors={campaignsResource.actions.resetErrors}
        items={campaigns}
        name={header}
        description={description}
        renderRowActions={renderRowActions}
        root={root}
        data-aid="campaigns-page"
      />

      {isDeleteModalVisible && (
        <StyledConfirmOrCancelModal
          data-aid="delete-campaign"
          onCancel={handleDeleteCancel}
          onSubmit={handleDeleteSubmit}
          submitButtonType={ButtonTypes.DANGER}
          submitText="Delete Campaign"
          text="This cannot be undone."
          title="Delete this campaign?"
        />
      )}
    </CampaignContext.Provider>
  );
};

export default ProactiveCommunication;

function getEditor(campaignType: CampaignType): JSX.Element | undefined {
  switch (campaignType) {
    case CampaignType.SMS:
      return <SmsCampaignContentEditor />;
    case CampaignType.EMAIL:
      return <EmailCampaignContentEditor />;
    case CampaignType.VOICE:
      return <VoiceCampaignContentEditor />;
  }
}
